// ============================================================
// SideQuests vNext - In-App Notifications
// Collection: notifications/{id} keyed by `uid`.
// Backend writes on: quest claimed, marked complete (poster review),
// payment released, disputed, authorization expiry, reported problems.
// Payment is approval-only: there is no automatic release.
// ============================================================

import { firestoreApi } from '../firebase';
import type { Unsubscribe } from 'firebase/firestore';
import type { Messaging } from 'firebase/messaging';
import { toast } from './toast';

// ============================================================
// SideQuests - Firebase Cloud Messaging (FCM)
// Service worker registration, permission, token, Firestore save,
// and foreground message → toast bridge.
//
// PERFORMANCE: `firebase/messaging` is now a DYNAMIC import. It is only
// reached from explicit, user-gesture notification flows (enableNotifications)
// or from the silent initFCM bootstrap that runs AFTER authentication
// resolves — never from the first-paint path of a public route.
// ============================================================

// Typed FCM data payload (matches functions/src/notifications.ts).
export interface FcmNotificationData {
  jobId?: string;
  type?: string;
  action?: string;
  icon?: string;
  image?: string;
  badge?: string;
  [key: string]: string | undefined;
}

const FCM_SW_PATH = '/firebase-messaging-sw.js';
let foregroundUnsubscribe: (() => void) | null = null;

export async function registerFCMServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(FCM_SW_PATH);
    console.log('FCM Service Worker registered:', reg);
    return reg;
  } catch (err) {
    console.error('FCM Service Worker registration failed:', err);
    return null;
  }
}

export async function requestNotificationPermission(): Promise<boolean | null> {
  if (typeof Notification === 'undefined') return null;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.log('Notification permission denied');
    return null;
  }
  return true;
}

export async function getFCMToken(): Promise<string | null> {
  try {
    const { getMessaging, getToken } = await import('firebase/messaging');
    const messaging = getMessaging();
    return await getToken(messaging, {
      vapidKey: 'BGQQZIrBBzvGERdMWO6nVO88ie5WsJ7UfIRBevQsvbl9eaKOZnIWAy0WAjJwj6qJVDg8U4lqD_jO1oiqbflmDjI',
    });
  } catch (err) {
    console.error('FCM token error:', err);
    return null;
  }
}

export async function saveFCMToken(uid: string, token: string): Promise<void> {
  const { db, doc, setDoc, serverTimestamp } = await firestoreApi();
  const ref = doc(db, 'users', uid, 'fcmTokens', token);
  await setDoc(ref, {
    token,
    createdAt: serverTimestamp(),
    deviceType: 'web',
  });
}

/**
 * 🔔 FOREGROUND PUSH → TOAST BRIDGE (FIX 2026-08-14).
 *
 * FCM messages delivered while the app tab is focused are NOT shown by the
 * browser (only background messages are handled by the service worker). The
 * app used to drop them silently. This subscribes once per session and
 * surfaces every foreground message as an in-app toast (the documented
 * fallback when system notifications can't render).
 *
 * Async because `onMessage` now comes from the lazily imported messaging SDK.
 */
export async function subscribeToForegroundMessages(messaging: Messaging): Promise<() => void> {
  if (foregroundUnsubscribe) return foregroundUnsubscribe;

  const toastByType: Record<string, string> = {
    quest_claimed: 'A Quester Answered the Call on your Quest!',
    quest_completed_approval: 'Approve payment or report a problem.',
    quest_approval_reminder: 'Payment is still waiting on your approval.',
    quest_completed: 'Quest completed!',
    payment_released: 'Payment released!',
    payment_disputed: 'A dispute paused the payout.',
    quest_issue_reported: 'A problem was reported; the payout is paused.',
    quest_issue_withdrawn: 'The report was withdrawn.',
    authorization_expiring: 'The card authorization expires soon.',
    authorization_expired: 'The card authorization expired; authorize again.',
    authorization_refreshed: 'A fresh card authorization is on file.',
    chat: 'New message from your quester.',
    xp: 'XP earned!',
    level_up: 'Level up!',
  };

  const { onMessage } = await import('firebase/messaging');

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    const title = payload.notification?.title || 'SideQuests';
    const body = payload.notification?.body || '';
    const data = (payload.data || {}) as FcmNotificationData;
    // Fallback message when the server only sends a type.
    const fallback = data.type && toastByType[data.type];
    if (body) toast(`${title}: ${body}`, 'info');
    else if (fallback) toast(`${title}: ${fallback}`, 'info');
    else if (title) toast(title, 'info');
    console.log('[SQ] Foreground FCM message:', { title, body, data });
  });

  return foregroundUnsubscribe;
}

export type NotificationEnableResult = 'granted' | 'denied' | 'unsupported' | 'failed';

/**
 * ⭐ EXPLICIT ENABLE FLOW (2026-08-21): the ONLY place the browser-native
 * permission API is ever called. Always invoked from a user gesture
 * (contextual "Enable notifications" button), never on page load.
 *
 * Returns a friendly status the UI can render: granted / denied /
 * unsupported / failed. No raw technical errors are surfaced to users.
 */
export async function enableNotifications(uid: string): Promise<NotificationEnableResult> {
  try {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
    await registerFCMServiceWorker();
    if (typeof Notification === 'undefined') return 'unsupported';

    // Browser-native permission prompt - only reached from a user tap.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    const token = await getFCMToken();
    if (token) {
      await saveFCMToken(uid, token);
      console.log('FCM ready:', token);
    }
    try {
      const { getMessaging } = await import('firebase/messaging');
      const messaging = getMessaging();
      await subscribeToForegroundMessages(messaging);
    } catch (e: any) {
      console.warn('[SQ] Foreground FCM subscription skipped:', e?.message || e);
    }
    return 'granted';
  } catch (err) {
    console.error('FCM enable error:', err);
    return 'failed';
  }
}

/**
 * Silent bootstrap - NEVER prompts, NEVER opens a modal.
 * Registers the service worker and, only if permission is already granted,
 * re-saves the token and bridges foreground messages to toasts.
 * Contextual "Enable notifications" prompts are triggered explicitly by
 * user actions (posting, claiming, visiting Messages), not here.
 */
export async function initFCM(uid: string): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    await registerFCMServiceWorker();

    // Already granted → go straight to token + foreground bridge.
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const token = await getFCMToken();
      if (token) {
        await saveFCMToken(uid, token);
        console.log('FCM ready:', token);
      }
      try {
        const { getMessaging } = await import('firebase/messaging');
        const messaging = getMessaging();
        await subscribeToForegroundMessages(messaging);
      } catch (e: any) {
        console.warn('[SQ] Foreground FCM subscription skipped:', e?.message || e);
      }
    }
    // Not granted → do nothing. No prompts from here, ever.
  } catch (err) {
    console.error('FCM init error:', err);
  }
}


export interface AppNotification {
  id: string;
  uid: string;
  title: string;
  body: string;
  type: string;
  jobId?: string;
  read: boolean;
  createdAt: Date;
}

export function subscribeNotifications(
  uid: string,
  callback: (notifications: AppNotification[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  // ⭐ P0 HARDENING (2026-08-20): a `where(uid ==)` + `orderBy(createdAt)`
  // query requires a composite index. If that index is missing in the
  // configured project, EVERY snapshot rejects and the alerts tab silently
  // stays stuck/blank. Drop the server-side orderBy (single-field query has
  // no composite-index requirement) and sort client-side with a safe
  // comparator that tolerates missing/invalid timestamps. Same ordering.
  let cancelled = false;
  let liveUnsub: Unsubscribe | null = null;

  firestoreApi().then(({ db, collection, query, where, limit, onSnapshot }) => {
    if (cancelled) return;
    const q = query(
      collection(db, 'notifications'),
      where('uid', '==', uid),
      limit(50)
    );

    liveUnsub = onSnapshot(q, (snap) => {
      const items: AppNotification[] = [];
      snap.forEach((d) => {
        try {
          const data = d.data() || {};
          let createdAt: Date;
          try {
            const parsed = (data.createdAt as { toDate?: () => Date } | undefined)?.toDate?.();
            createdAt = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
          } catch {
            createdAt = new Date();
          }
          items.push({
            id: d.id,
            uid: typeof data.uid === 'string' ? data.uid : '',
            title: typeof data.title === 'string' ? data.title : '',
            body: typeof data.body === 'string' ? data.body : '',
            type: typeof data.type === 'string' ? data.type : 'system',
            jobId: typeof data.jobId === 'string' ? data.jobId : undefined,
            read: !!data.read,
            createdAt,
          });
        } catch (err) {
          console.warn('[SQ] Skipping malformed notification doc:', d.id, err);
        }
      });
      // Newest first; missing timestamps sort last.
      items.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
      callback(items);
    }, onError);
  }).catch((e) => onError?.(e as Error));

  return () => {
    cancelled = true;
    liveUnsub?.();
  };
}

export async function markNotificationRead(id: string) {
  const { db, doc, updateDoc } = await firestoreApi();
  await updateDoc(doc(db, 'notifications', id), { read: true });
}

export async function markAllNotificationsRead(uid: string) {
  const { db, collection, query, where, limit, getDocs, writeBatch } = await firestoreApi();
  const q = query(
    collection(db, 'notifications'),
    where('uid', '==', uid),
    where('read', '==', false),
    limit(100)
  );
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.forEach((d) => batch.update(d.ref, { read: true }));
  await batch.commit();
}

export function unreadCount(notifications: AppNotification[]): number {
  return notifications.filter((n) => !n.read).length;
}
